import { Grid } from "semantic-ui-react";

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import useWindowSize from "react-use/lib/useWindowSize";

import Flex from "../../../../components/Flex/Flex";
import AlertToggle from "../../../../components/production/AlertToggle";
import StarToggle from "../../../../components/production/StarToggle";
import MigrateProduction from "../../../../components/production/parts/MigrateProduction";
import HistoryModal from "../../../../components/production/parts/ProductionHistory";
import ProductionInformationEdit from "../../../../components/production/parts/ProductionInformationEdit";
import CoverImage from "../../../ui/components/CoverImage/CoverImage";
import DropdownMenu from "../../../ui/components/DropdownMenu/DropdownMenu";
import VerticalMenu from "../../../ui/components/VerticalMenu/VerticalMenu";
import useMenuDocTitle from "../../../ui/hooks/useMenuDocTitle";
import PageView from "../../../ui/views/PageView/PageView";

import { useProfile } from "../../../authentication/state/profile";
import useHasRight from "../../../authorization/hooks/useHasRight";

import ProductionStatusLabel from "../../components/ProductionStatus/ProductionStatusLabel";
import ProductionTodos from "../../components/ProductionTodos/ProductionTodos";
import useProductionPermissions from "../../hooks/useProductionPermissions";
import updateProduction from "../../utils/updateProduction";

import ProductionDiscussionsView from "../ProductionDiscussionsView/ProductionDiscussionsView";

import ProductionDiscussionOld from "./components/ProductionDiscussionOld";
import ProductionViewContext from "./context/ProductionViewContext";
import useProductionDropdownMenuOptions from "./hooks/useProductionDropdownMenuOptions";
import useProductionMenuOptions from "./hooks/useProductionMenuOptions";
import useProductionMetaGuide from "./hooks/useProductionMetaGuide";

export default function ProductionView({
	modal,
	offers,
	history,
	producer,
	publisher,
	production,
	discussionChannels,
	productionTodos,
	children,
}) {
	const user = useProfile();
	const navigate = useNavigate();
	const hasRight = useHasRight();

	const { width } = useWindowSize();

	const [historyOpen, setHistoryOpen] = useState(false);
	const [migrateProductionOpen, setMigrateProductionOpen] = useState(false);

	const isDesktop = width > 1000;

	const permissions = useProductionPermissions(production);

	const handleChange = (e, data, options) => updateProduction(e, production, data, options);

	const alerts = useProductionMetaGuide({ history, production });
	const menuOptions = useProductionMenuOptions({ production, offers, alerts, discussionChannels });
	const dropdownMenuOptions = useProductionDropdownMenuOptions({
		production,
		setHistoryOpen,
		setMigrateProductionOpen,
	});

	useMenuDocTitle(production.title, menuOptions);

	return (
		<>
			<PageView
				image={<CoverImage thumb data={production} size={70} />}
				header={production.title}
				subheader={`${production.publisherName}${
					production.imprint?.name ? `/${production.imprint.name}` : ""
				} + ${production.producerName}`}
				extra={
					<Flex justifyContent="flex-end" gap={10}>
						<ProductionStatusLabel status={production.status} />

						<StarToggle production={production.id} profile={user} uid={user?.id} />

						<AlertToggle production={production} profile={user} uid={user?.id} />

						<DropdownMenu style={{ marginRight: "-10px" }} options={dropdownMenuOptions} />
					</Flex>
				}
				sidebar={<VerticalMenu options={menuOptions} />}
				sidebarWidth={2}
				maxWidth={2000}
			>
				<Grid>
					<Grid.Column width={isDesktop ? 10 : 16}>
						<ProductionViewContext.Provider
							value={{
								alerts,
								offers,
								producer,
								publisher,
								isDesktop,
								permissions,
								handleChange,
								fullProduction: production,
							}}
						>
							{children}
						</ProductionViewContext.Provider>
					</Grid.Column>

					{modal === "edit" && (
						<ProductionInformationEdit
							alerts={alerts}
							producer={producer}
							publisher={publisher}
							production={production}
							permissions={permissions}
							handleChange={handleChange}
							onClose={() => navigate(`/production/${production.id}`)}
						/>
					)}

					<Grid.Column width={isDesktop ? 6 : 16}>
						{isDesktop &&
							!history?.location?.pathname?.endsWith("discussions") &&
							(production.useNewDiscussion ? (
								<ProductionDiscussionsView production={production} />
							) : (
								<ProductionDiscussionOld production={production} permissions={permissions} />
							))}

						{hasRight("productionTodos.manage") && (
							<ProductionTodos production={production} todos={productionTodos} />
						)}
					</Grid.Column>
				</Grid>
			</PageView>

			{historyOpen && (
				<HistoryModal
					productionId={production.id}
					onClose={() => {
						setHistoryOpen(false);
					}}
				/>
			)}

			{migrateProductionOpen && (
				<MigrateProduction production={production} setMigrateProductionOpen={setMigrateProductionOpen} />
			)}
		</>
	);
}
